import loading from 'assets/pictures/loading.gif'
import { push } from '@lagunovsky/redux-react-router'
import React, { useEffect, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { orderBy, sortBy } from 'lodash'
import DtoSimpleStation from 'home/dto/DtoSimpleStation'
import { getDate } from '../../utils/DateUtil'
import MonitoredMaterielPortletPanel from '../../materiel/components/dashboard/MonitoredMaterielPortletPanel'
import { getUserBookmarksByStationType } from '../../utils/UserUtil'
import { componentHasHabilitations } from '../../utils/HabilitationUtil'
import {
    H_MAT_MODULE,
    H_QUALITO_MODULE,
} from '../../account/constants/AccessRulesConstants'
import HabilitationRequired from '../../utils/components/HabilitationRequired'
import { STATION_TYPE_CONSTANT, STATION_TYPE_NAME } from 'station/constants/StationConstants'
import UserThunk from 'administration/components/user/actions/UserThunk'
import QualityThunk from 'quality/actions/QualityThunk'
import CampaignThunk from 'campaign/actions/CampaignThunk'
import useStateProgress from 'utils/customHook/useStateProgress'
import { CampaignsPanel } from './qualitometers/QualitometersDashboardApp'
import { Card, CardContent, Grid, Stack } from '@mui/material'
import { NewTable } from '../../components/datatable/NewTable'
import { CardTitle } from '../../components/card/NewCard'
import PieChartIcon from '@mui/icons-material/PieChart'
import { darkBlue } from '../../utils/constants/ColorTheme'
import ProgressBar from '../../components/progress/ProgressBar'

const QualityCard = () => {
    const {
        userBookmarks,
        qualitometers,
        qualityCampaigns,
        monitoredMateriels,
        citiesIndex,
        userBookmarksStatus,
        qualitometersListSpecificStatus,
        qualityCampaignsStatus,
        contacts,
        monitoredStations,
    } = useSelector(store => ({
        userBookmarks: store.UserReducer.userBookmarks,
        qualitometers: store.QualityReducer.qualitometersListSpecific,
        qualityCampaigns: store.QualityReducer.qualityCampaigns,
        monitoredMateriels: store.MaterielReducer.monitoredMateriels,
        citiesIndex: store.CityReducer.citiesIndex,
        userBookmarksStatus: store.DataManagerReducer.user.userBookmarksStatus,
        qualitometersListSpecificStatus: store.DataManagerReducer.quality.qualitometersListSpecificStatus,
        qualityCampaignsStatus: store.DataManagerReducer.campaign.qualityCampaignsStatus,
        contacts: store.ContactReducer.contacts,
        monitoredStations: store.EventsReducer.monitoredStations,
    }), shallowEqual)

    const { progress: progressBar, isLoaded: dataLoaded } = useStateProgress([
        userBookmarksStatus,
        qualitometersListSpecificStatus,
        qualityCampaignsStatus,
    ])

    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(UserThunk.fetchBookmarks())
        dispatch(QualityThunk.fetchQualitometersListSpecific())
        dispatch(CampaignThunk.fetchQualityCampaigns())
    }, [])

    const bookmarks = useMemo(() => getUserBookmarksByStationType(userBookmarks, STATION_TYPE_NAME.quality), [userBookmarks])

    const stationsForTable = useMemo(() => {
        const bookmarksStations = bookmarks.map(code => qualitometers.find(s => s.code === code)).filter(p => !!p).map(s => ({ ...s, isBookmark: true }))
        const monitored = monitoredStations.filter(s => s.type === STATION_TYPE_CONSTANT.QUALITY && qualitometers.some(s2 => s.id === s2.id))
            .map(s => ({ ...s, isMonitored: true, ...qualitometers.find(s2 => s.id === s2.id) }))
        const reduced = [...bookmarksStations, ...monitored].reduce((acc, s) => {
            const found = acc.find(s2 => s.id === s2.id)
            if (found) {
                return [...acc.filter(s2 => s2.id !== s.id), { ...s, ...found }]
            }
            return [...acc, s]
        }, [])
        return orderBy(reduced, [s => s.isMonitored && s.isBookmark ? 2 : (s.isMonitored ? 1 : 0), 'name'], ['desc', 'asc'])
    }, [qualitometers, bookmarks, monitoredStations])

    const data = useMemo(() => ({
        stations: qualitometers,
        bookmarks,
        icon: 'pie_chart',
        campaigns: qualityCampaigns,
        stationType: STATION_TYPE_CONSTANT.QUALITY,
        habilitation: componentHasHabilitations(H_QUALITO_MODULE),
        monitoredMateriels: monitoredMateriels.filter(m => m.typeSite === STATION_TYPE_CONSTANT.QUALITY),
    }), [bookmarks, qualityCampaigns, contacts, monitoredMateriels, qualitometers])

    const bookmarksStationPanel = useMemo(() => {
        const tableData = sortBy(stationsForTable.map(station => {
            const cityFound = citiesIndex[station.townCode]
            const result = new DtoSimpleStation(station)
            return {
                ...result,
                city: cityFound ? `${cityFound.name} [${cityFound.code}]` : station.townCode,
                historic: station.historyStart && station.historyEnd ? `${getDate(station.historyStart)} ${i18n.to} ${getDate(station.historyEnd)}` : '',
                operations: station.operationCount,
                comment: station.eventComment,
                nullValue: {
                    value: '',
                    align: 'center',
                    leftIcon: station.isMonitored && station.isBookmark ? {
                        icon: 'notificationsNone',
                        color: 'red',
                        tooltip: i18n.oversight,
                    } : undefined,
                    rightIcon: {
                        icon: station.isBookmark ? 'star' : 'notificationsNone',
                        color: station.isBookmark ? 'orange' : 'red',
                        tooltip: station.isBookmark ? i18n.bookmark : i18n.oversight,
                    },
                },
            }
        }), 'name')
        return (
            <NewTable
                headers={ ['nullValue', 'code', 'city', 'name', 'stationType', 'historic', 'operations', 'comment'] }
                rows={tableData}
                onClickRow={s => dispatch(push(`/station/quality/${s.id}`))}
            />
        )
    }, [citiesIndex, stationsForTable])

    const bookmarksPanel = useMemo(() => {
        if (stationsForTable.length > 0) {
            return bookmarksStationPanel
        }
        return null
    }, [bookmarksStationPanel, stationsForTable.length])

    const campaignsPanel = useMemo(() => {
        if (data.campaigns?.length > 0) {
            return (
                <CampaignsPanel campaigns={data.campaigns} />
            )
        }
        return null
    }, [data.campaigns])

    if (dataLoaded) {
        if (!(data.habilitation && ((data.bookmarks.length > 0 && data.stations.length > 0) || data.campaigns.length > 0 || data.monitoredMateriels.length > 0 || stationsForTable.length > 0))) {
            return null
        }
        return (
            <>
                <Card>
                    <CardTitle
                        title={i18n[STATION_TYPE_NAME.quality]}
                        icon={<PieChartIcon fontSize='large'/>}
                        color={darkBlue}
                        onClick={() => dispatch(push(`/${data.redirect || STATION_TYPE_NAME.quality}`))}
                    />
                    <Grid container>
                        <Stack sx={{ width: '100%' }} spacing={1}>
                            {bookmarksPanel}
                            {campaignsPanel}
                        </Stack>
                        <HabilitationRequired habilitation={H_MAT_MODULE}>
                            <MonitoredMaterielPortletPanel
                                noHeader
                                stationType={data.stationType}
                            />
                        </HabilitationRequired>
                    </Grid>
                </Card>
            </>
        )
    }
    return (
        <Card>
            <CardContent>
                <Grid
                    container
                    justifyContent='center'
                    alignItems='center'
                    direction='column'
                    sx={{ pt: 2.5 }}
                >

                    <img src={loading} />
                    <Grid size={3}>
                        <ProgressBar
                            withMessage
                            message={i18n.qualityCardInProgress}
                            progress={progressBar}
                        />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

export default QualityCard